import { MedicalCaseProcessingPhase } from '../../../domain/medical-case/model';
import { PreparationStepKey, StepOutdatedRule } from './pipeline-step.model';

/**
 * Single source of truth for wayback backend phase bindings.
 *
 * Active phases (rebound to SECOND_OPINION_*):
 *   upload, digitization, anonymize, summary, glossary,
 *   final-report, translate, deanonymize, package
 */

// ── Step → Backend Phase ───────────────────────────────────────────

export const WAYBACK_STEP_TO_PHASE: Readonly<Partial<Record<PreparationStepKey, MedicalCaseProcessingPhase>>> = {
  upload: 'SECOND_OPINION_INIT',
  digitization: 'SECOND_OPINION_DIGITIZATION',
  anonymize: 'SECOND_OPINION_ANONYMIZATION',
  summary: 'SECOND_OPINION_SUMMARY',
  glossary: 'SECOND_OPINION_GLOSSARY',
  'final-report': 'SECOND_OPINION_FINAL_REPORT',
  translate: 'SECOND_OPINION_FINAL_REPORT_TRANSLATION',
  deanonymize: 'SECOND_OPINION_FINAL_REPORT_DEANONYMIZATION',
  package: 'SECOND_OPINION_FINAL_REPORT_PDF',
};

// ── Processing boundaries per actionable step ──────────────────────

export const WAYBACK_PROCESSING_BOUNDARIES: Readonly<Record<string, readonly [start: MedicalCaseProcessingPhase, end: MedicalCaseProcessingPhase]>> = {
  digitization: ['SECOND_OPINION_INIT', 'SECOND_OPINION_DIGITIZATION'],
  anonymize: ['SECOND_OPINION_DIGITIZATION', 'SECOND_OPINION_ANONYMIZATION'],
  summary: ['SECOND_OPINION_ANONYMIZATION', 'SECOND_OPINION_SUMMARY'],
  glossary: ['SECOND_OPINION_SUMMARY', 'SECOND_OPINION_GLOSSARY'],
  'final-report': ['SECOND_OPINION_GLOSSARY', 'SECOND_OPINION_FINAL_REPORT'],
  translate: ['SECOND_OPINION_FINAL_REPORT', 'SECOND_OPINION_FINAL_REPORT_TRANSLATION'],
  deanonymize: ['SECOND_OPINION_FINAL_REPORT_TRANSLATION', 'SECOND_OPINION_FINAL_REPORT_DEANONYMIZATION'],
  package: ['SECOND_OPINION_FINAL_REPORT_DEANONYMIZATION', 'SECOND_OPINION_FINAL_REPORT_PDF'],
};

// ── Phase orders for document resolution ────────────────────────────

export const WAYBACK_DOCUMENT_PHASE_ORDER: readonly MedicalCaseProcessingPhase[] = [
  'SECOND_OPINION_INIT',
  'SECOND_OPINION_DIGITIZATION',
  'SECOND_OPINION_ANONYMIZATION',
  'SECOND_OPINION_SUMMARY',
  'SECOND_OPINION_GLOSSARY',
  'SECOND_OPINION_FINAL_REPORT',
  'SECOND_OPINION_FINAL_REPORT_TRANSLATION',
  'SECOND_OPINION_FINAL_REPORT_DEANONYMIZATION',
  'SECOND_OPINION_FINAL_REPORT_PDF',
];

export const WAYBACK_ALL_PHASE_ORDER: readonly MedicalCaseProcessingPhase[] = [
  'SECOND_OPINION_INIT',
  'SECOND_OPINION_CONVERSION',
  'SECOND_OPINION_DIGITIZATION',
  'SECOND_OPINION_ANONYMIZATION',
  'MEDICAL_RECORD_ANONYMIZED_SUMMARY',
  'SECOND_OPINION_SUMMARY',
  'SECOND_OPINION_GLOSSARY',
  'SECOND_OPINION_FINAL_REPORT',
  'SECOND_OPINION_FINAL_REPORT_TRANSLATION',
  'SECOND_OPINION_FINAL_REPORT_DEANONYMIZATION',
  'SECOND_OPINION_FINAL_REPORT_PDF',
];

// ── Outdated rule phase dependencies ────────────────────────────────

export const WAYBACK_OUTDATED_RULES: Readonly<Partial<Record<PreparationStepKey, StepOutdatedRule>>> = {
  summary: { dependencyPhases: ['SECOND_OPINION_ANONYMIZATION', 'MEDICAL_RECORD_ANONYMIZED_SUMMARY'], nullGuardPhase: 'SECOND_OPINION_SUMMARY' },
  glossary: { dependencyPhases: ['SECOND_OPINION_ANONYMIZATION', 'MEDICAL_RECORD_ANONYMIZED_SUMMARY'], nullGuardPhase: 'SECOND_OPINION_SUMMARY' },
};
